import { z } from 'zod';
import { stampedBody } from '../common/row-version.js';
import { csv, ENABLED_FILTER_STATES, queryBool } from '../common/query.js';
import { PaginationSchema } from '../common/pagination.js';
// The teams module's wire (ADR-083) — fixed people of ONE project: members who
// can be assigned or not, team-level responsibilities resting on membership, a
// work area ("Works in"), an optional time zone. Every route is behind
// `has_field_force`; who sees and runs a team is `team:read` / `team:manage`
// over TEAM SCOPE on the person, and a member sees their own teams by
// membership alone (no key).
const Id16 = z.string().min(1).max(16);
const IdList = z.array(Id16).max(500);
/** The list's order keys: the name (A–Z) or the member count. The default order puts the viewer's
 *  own teams first, then the name — "My teams" first (ADR-083 §11). */
export const TEAM_SORT_KEYS = ['name', 'members'];
// ── GET /teams · /teams/count ────────────────────────────────────────────────
export const TeamsListQuerySchema = PaginationSchema.extend({
    /** Typed search on the NAME (ADR-072 — a typed mistake forgiven). ≤ 64 chars. */
    q: z.string().trim().max(64).optional(),
    /** The Mine chip — the teams the viewer is a MEMBER of (membership, never a key or a grant). */
    mine: queryBool,
    /** Active, disabled, or both; absent = every team, the disabled ones marked. */
    status: csv(z.enum(ENABLED_FILTER_STATES)),
    sort_by: z.enum(TEAM_SORT_KEYS).optional(),
    sort_dir: z.enum(['asc', 'desc']).optional(),
}).strict();
// ── GET /teams/names — the small list a picker asks whole (ADR-080 §3) ───────
/** Which teams: `all` = every team of the project (the naming rule — every member sees team NAMES,
 *  ADR-026 §A); `grantable` = the teams inside the viewer's own TEAM SCOPE (the scope editor's
 *  Teams section — a giver grants only what they hold, ADR-050 §3). */
export const TEAM_NAMES_RULES = ['all', 'grantable'];
export const TeamNamesQuerySchema = z.object({ rule: z.enum(TEAM_NAMES_RULES).default('all') }).strict();
// ── POST /teams · PATCH /teams/:teamId ───────────────────────────────────────
/** The time zone is an IANA zone name the runtime accepts (the one shared check, ADR-030);
 *  null = the project's (ADR-082 §2 — "works on the project's clock"). */
const TimezoneSchema = z.string().trim().min(1).max(64).nullable();
export const CreateTeamRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(64),
    timezone_iana: TimezoneSchema.default(null),
})
    .strict();
export const UpdateTeamRequestSchema = z
    .object({
    ...stampedBody,
    name: z.string().trim().min(1).max(64).optional(),
    timezone_iana: TimezoneSchema.optional(),
})
    .strict();
// ── the members ──────────────────────────────────────────────────────────────
/** POST /teams/:teamId/members — add members (active members of the project; a person already on
 *  the team is left as they are). At most 50 a request. */
export const AddTeamMembersRequestSchema = z
    .object({
    user_ids: z.array(Id16).min(1).max(50),
    /** Default on: offered as a doer. Off for a manager or a NOC person who sees and talks. */
    can_be_assigned: z.boolean().default(true),
})
    .strict();
/** PATCH /teams/:teamId/members/:userId — whether the member can be assigned. */
export const UpdateTeamMemberRequestSchema = z.object({ can_be_assigned: z.boolean() }).strict();
// ── PUT /teams/:teamId/work-area — "Works in" (ADR-083 §6) ───────────────────
/** The places a team's tickets can be at: the whole project, or the places by kind. The whole
 *  project absorbs every place (the pair's wildcard grammar); the server stores one row with no
 *  place set. Empty everywhere = the team works nowhere yet. */
export const TeamWorkAreaRequestSchema = z
    .object({
    ...stampedBody,
    whole_project: z.boolean().default(false),
    zone_ids: z.array(z.number().int().positive()).max(500).default([]),
    cluster_ids: z.array(z.number().int().positive()).max(500).default([]),
    site_ids: IdList.default([]),
    warehouse_ids: IdList.default([]),
    vehicle_pool_ids: IdList.default([]),
})
    .strict();
